import type { PDPProductThumbnail } from "@components/commerce/product/right-panel/buy-card/types";
import type { BrowseOptions } from "@algolia/client-search";
import type { SearchOptions } from "instantsearch.js";
import type { RequestOptions } from "@algolia/transporter";
import type { SearchClient, SearchIndex } from "algoliasearch";

export interface AlgoliaBaseProduct {
    [AlgoliaProductProperties.articleNumber]: string;
    [AlgoliaProductProperties.productColors]: ProductColor[];
    [AlgoliaProductProperties.objectID]: string;
    [AlgoliaProductProperties.colorCode]: string;
    [AlgoliaProductProperties.categories]: string[];
    [AlgoliaProductProperties.hasDiscount]: boolean;
    [AlgoliaProductProperties.eaBadge]?: SaleBadge;
    [AlgoliaProductProperties.image]: string;
    [AlgoliaProductProperties.imageLabel]: string;
    [AlgoliaProductProperties.hasStock]: boolean;
    [AlgoliaProductProperties.name]: string;
    [AlgoliaProductProperties.priceRaw]: PriceRaw;
    [AlgoliaProductProperties.listPriceRaw]: PriceRaw;
    [AlgoliaProductProperties.lowestPriceRaw]?: PriceRaw;
    [AlgoliaProductProperties.discountPercentageLowestPrice]?: number;
    [AlgoliaProductProperties.priceWithDiscount]: number;
    [AlgoliaProductProperties.priceWithoutDiscount]: number;
    [AlgoliaProductProperties.variants]: Variant[];
    [AlgoliaProductProperties.discountPercentage]: number;
    [AlgoliaProductProperties.backgroundColor]: string;
    [AlgoliaProductProperties.badges]?: string[];
    [AlgoliaProductProperties.listPrice]: number;
    [AlgoliaProductProperties.bestseller]: boolean;
    [AlgoliaProductProperties.onlineExclusive]: boolean;
    [AlgoliaProductProperties.group]: string;
    [AlgoliaProductProperties.collectionClass]: string;
    [AlgoliaProductProperties.collectionType]: string;
}

export interface AlgoliaProduct extends AlgoliaBaseProduct {
    [AlgoliaProductProperties.dominantColor]: string;
    [AlgoliaProductProperties.pageTitle]: string;
    [AlgoliaProductProperties.descriptiveHeading]: string;
    [AlgoliaProductProperties.gender]: string;
    [AlgoliaProductProperties.materialGroup]: string;
    [AlgoliaProductProperties.collectionSegment]: string;
    [AlgoliaProductProperties.collectionClass]: string;
    [AlgoliaProductProperties.hierarchicalCategories]: HierarchicalCategories;
    [AlgoliaProductProperties.sizes]: string[];
    [AlgoliaProductProperties.factsAndBenefits]: string[];
    [AlgoliaProductProperties.priceRange]: string;
    [AlgoliaProductProperties.appVersion]: string;
    [AlgoliaProductProperties.fabricContent]: string;
    [AlgoliaProductProperties.occasion]: string;
    [AlgoliaProductProperties.season]?: string;
    [AlgoliaProductProperties.targetGroup]: string;
    [AlgoliaProductProperties.group]: string;
    [AlgoliaProductProperties.images]: string[];
    [AlgoliaProductProperties.productVideo]?: string;
    [AlgoliaProductProperties.interactiveVideo]?: string;
    [AlgoliaProductProperties.productNameCustomerFriendly]: string;
    [AlgoliaProductProperties.division]: string;
    [AlgoliaProductProperties.collectionType]: string;
    [AlgoliaProductProperties.collectionCategory]: string;
    [AlgoliaProductProperties.fullSizes]: FullSize[];
    [AlgoliaProductProperties.lastUpdatedAt]: string;
    [AlgoliaProductProperties.lastSyncedAt]: string;
    [AlgoliaProductProperties.returnRate1m]?: number;
    [AlgoliaProductProperties.soldQty1m]?: number;
    [AlgoliaProductProperties.margin1m]?: number;
    [AlgoliaProductProperties.clickThroughRate1m]?: number;
    [AlgoliaProductProperties.conversionRate1m]?: number;
    [AlgoliaProductProperties.removeableInsole]?: boolean;
    [AlgoliaProductProperties.googleProductFeed]?: string;
    [AlgoliaProductProperties.dominantMaterial]?: string;
    [AlgoliaProductProperties.consumerType]?: string;
    [AlgoliaProductProperties.price]?: number;
    [AlgoliaProductProperties.id]: string;
    [AlgoliaProductProperties.__queryID]?: string;
}

export declare type BrowseParams = SearchOptions & BrowseOptions<AlgoliaProduct> & RequestOptions;

export type AlgoliaClient = SearchClient & {
    index: SearchIndex;
    browseObjects: (params: BrowseParams) => Readonly<Promise<void>>;
};

export interface PriceRaw {
    currencyCode: string;
    centAmount: number;
    fractionDigits: number;
}

export interface HierarchicalCategories {
    lvl0: string[];
    lvl1: string[];
    lvl2: string[];
}

export interface ProductColor {
    image: string;
    objectID: string;
    price: number;
    listPrice: number;
    colorCode: string;
    priceRaw: Price;
    listPriceRaw: Price;
    lowestPriceRaw: Price;
    discountPercentage: number;
    discountPercentageLowestPrice: number;
    backgroundColor: string;
    hasStock: boolean;
    hasDiscount: boolean;
    eaBadge?: SaleBadge;
    imageLabel: string;
    dominantColor: string;
    name: string;
}

export interface SaleBadge {
    badge: string;
    from: string; // ISO 8601 date string
    until: string; // ISO 8601 date string
}

export interface Price {
    currencyCode: string;
    centAmount: number;
    fractionDigits: number;
}

export interface Variant {
    size: string;
    sizeUS: string;
    sizeUK: string;
    sizeCN: string;
    sizeKR: string;
    sizeJP: string;
    sizeCA: string;
    availableQuantity: number;
    sku: string;
    hasStock: boolean;
    sitecoreId?: string;
}

export interface FullSize {
    eu: string;
    us: string;
    uk: string;
    cn: string;
    kr: string;
    jp: string;
    ca: string;
}

export interface AlgoliaRelatedProduct {
    variants: Variant[];
    categories: string[];
    imageThumbnails: PDPProductThumbnail[];
    eaBadge?: SaleBadge;
}

export enum AlgoliaProductProperties {
    name = "name",
    articleNumber = "articleNumber",
    pageTitle = "pageTitle",
    image = "image",
    price = "price",
    listPrice = "listPrice",
    priceRaw = "priceRaw",
    listPriceRaw = "listPriceRaw",
    hasDiscount = "hasDiscount",
    eaBadge = "eaBadge",
    colorCode = "colorCode",
    dominantColor = "dominantColor",
    descriptiveHeading = "descriptiveHeading",
    gender = "gender",
    materialGroup = "materialGroup",
    collectionSegment = "collectionSegment",
    collectionClass = "collectionClass",
    hierarchicalCategories = "hierarchicalCategories",
    categories = "categories",
    productColors = "productColors",
    variants = "variants",
    sizes = "sizes",
    factsAndBenefits = "factsAndBenefits",
    priceRange = "priceRange",
    backgroundColor = "backgroundColor",
    discountPercentage = "discountPercentage",
    lowestPriceRaw = "lowestPriceRaw",
    discountPercentageLowestPrice = "discountPercentageLowestPrice",
    bestseller = "bestseller",
    onlineExclusive = "onlineExclusive",
    appVersion = "appVersion",
    hasStock = "hasStock",
    fabricContent = "fabricContent",
    occasion = "occasion",
    season = "season",
    targetGroup = "targetGroup",
    group = "group",
    images = "images",
    productVideo = "productVideo",
    interactiveVideo = "interactiveVideo",
    productNameCustomerFriendly = "productNameCustomerFriendly",
    division = "division",
    collectionType = "collectionType",
    collectionCategory = "collectionCategory",
    fullSizes = "fullSizes",
    imageLabel = "imageLabel",
    lastUpdatedAt = "lastUpdatedAt",
    lastSyncedAt = "lastSyncedAt",
    objectID = "objectID",
    badges = "badges",
    priceWithDiscount = "priceWithDiscount",
    priceWithoutDiscount = "priceWithoutDiscount",
    lowestPrice = "lowestPrice",
    altTagPattern = "altTagPattern",
    returnRate1m = "returnRate1m",
    soldQty1m = "soldQty1m",
    margin1m = "margin1m",
    clickThroughRate1m = "clickThroughRate1m",
    conversionRate1m = "conversionRate1m",
    removeableInsole = "removeableInsole",
    googleProductFeed = "googleProductFeed",
    dominantMaterial = "dominantMaterial",
    consumerType = "consumerType",
    id = "id",
    __queryID = "__queryID",
}
